/**
 * The state the four Edit Contest steps share, held on the layout route so it
 * outlives any one of them.
 *
 * Before CAE-1548 all of this lived in `contest-form-page.tsx` alongside the tab
 * it was editing, which worked because the tabs were local state and nothing
 * ever unmounted. Steps are URLs now: navigating to Fixtures unmounts Basic
 * Info, and a half-typed name held there would go with it. The provider sits
 * above the `<Outlet/>`, so it does not.
 *
 * It owns what the *whole contest* is editing — the basic-info fields, the save
 * mutation, validation, the confirm dialog, and the notice strip the panels push
 * messages onto. Each step's own concerns (which reel is open, which fixtures
 * are selected) stay in that step.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contestsApi, type ContestDetail } from '../../../api/contests';
import { AdminApiError } from '../../../api/client';
import {
  buildContestAdapterConfig,
  parseContestAdapterFields,
} from '../../../utils/contest-adapter-config';
import {
  isParseableLocalDateTime,
  requiredLocalDateTimeFieldError,
  requiredTrimmedTextFieldError,
} from '../../../utils/form-validation';
import { DEFAULT_REEL_COUNT } from '../../../contests/readiness';
import type { BasicInfoFields } from '../basic-info';

/**
 * The ten fields Basic Info draws, which is exactly what this form holds.
 *
 * Aliased rather than restated: `<BasicInfoSections>` is what renders them, on
 * this screen and on New Contest both, so a field added there and not here
 * would be a field the form could not hold.
 */
export type ContestEditFields = BasicInfoFields;

const EMPTY_FIELDS: ContestEditFields = {
  sportId: '',
  reelCount: '',
  nameEn: '',
  superTitle: '',
  subTitle: '',
  description: '',
  campaignId: '',
  round: '',
  date: '',
  opensAt: '',
};

const toIsoFromLocalInput = (value: string): string => new Date(value).toISOString();

const toNullableString = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * `datetime-local` wants wall-clock in the browser's zone, not an instant, so
 * the ISO the API returns is split by local parts rather than sliced off the
 * UTC string — which would shift the time for any operator east or west of UTC.
 */
const toLocalDateTimeInput = (iso: string): string => {
  const date = new Date(iso);
  const pad = (value: number) => `${value}`.padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

interface ContestEditValue {
  contestId: string;
  detail: ContestDetail | null;
  isLoading: boolean;
  loadError: unknown;
  /** True once the contest's saved values have been copied into the form. */
  isHydrated: boolean;

  fields: ContestEditFields;
  /**
   * Curried on the key so a field can be wired with one reference
   * (`onChange={setField('nameEn')}`) rather than a closure per render.
   *
   * Takes the value, not the change event. The screens that read this build on
   * `basic-info/field.tsx`, whose controls hand back a string — an
   * event-shaped setter made every call site either synthesise a fake
   * `{ target: { value } }` or reach through a real one for the same string.
   */
  setField: <K extends keyof ContestEditFields>(key: K) => (value: string) => void;
  /** Locked after the first prize delivery — campaign ID and round go read-only. */
  adapterConfigFieldsLocked: boolean;

  nameEnError: string | null;
  opensAtError: string | null;
  isValid: boolean;
  markFieldBlurred: (field: 'nameEn' | 'opensAt') => void;
  /** True once a save has been attempted, which un-hides the inline field errors. */
  submitAttempted: boolean;

  save: () => void;
  isSaving: boolean;
  discard: () => void;
  /** True when the form differs from what the contest was loaded with. */
  isDirty: boolean;

  notice: string | null;
  setNotice: (notice: string | null) => void;
  errorMessage: string | null;
  resetError: () => void;

  /** The invalidation key every panel's mutation has to bust. */
  detailQueryKey: readonly unknown[];
}

const ContestEditContext = createContext<ContestEditValue | null>(null);

export const useContestEdit = (): ContestEditValue => {
  const value = useContext(ContestEditContext);
  if (!value) {
    throw new Error('useContestEdit must be used inside <ContestEditProvider>');
  }
  return value;
};

interface ContestEditProviderProps {
  contestId: string;
  children: ReactNode;
}

export const ContestEditProvider = ({ contestId, children }: ContestEditProviderProps) => {
  const queryClient = useQueryClient();
  const detailQueryKey = useMemo(
    () => ['admin', 'contests', contestId, 'edit-form'] as const,
    [contestId],
  );

  const [fields, setFields] = useState<ContestEditFields>(EMPTY_FIELDS);
  const [savedFields, setSavedFields] = useState<ContestEditFields>(EMPTY_FIELDS);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [blurred, setBlurred] = useState({ nameEn: false, opensAt: false });
  /*
   * State rather than a ref, because the chrome *renders* from it: the rail and
   * the Save button's disabled reason are both wrong while the form still holds
   * `EMPTY_FIELDS`, so they gate on this. A ref read during render would happen
   * to work — `setFields` re-renders anyway — but only by accident, and the
   * accident is invisible at the call site.
   */
  const [isHydrated, setIsHydrated] = useState(false);

  const {
    data: detail,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => contestsApi.get(contestId),
  });

  /**
   * Hydrates once, not on every refetch. A panel's mutation invalidates this
   * query — attaching a fixture, enabling an option — and re-seeding the form
   * each time would throw away whatever the operator had typed and not saved.
   */
  useEffect(() => {
    if (!detail || isHydrated) return;
    const adapterFields = parseContestAdapterFields(detail.contest.adapterConfig);
    const hydrated: ContestEditFields = {
      sportId: detail.contest.sportId ?? 'football',
      reelCount: String(detail.contest.reelCount ?? DEFAULT_REEL_COUNT),
      nameEn: detail.contest.name.en,
      superTitle: detail.contest.superTitle ?? '',
      subTitle: detail.contest.subTitle ?? '',
      description: detail.contest.description ?? '',
      campaignId: adapterFields.campaignId,
      round: adapterFields.round,
      date: detail.contest.date,
      opensAt: toLocalDateTimeInput(detail.contest.opensAt),
    };
    setFields(hydrated);
    setSavedFields(hydrated);
    setIsHydrated(true);
  }, [detail, isHydrated]);

  /*
   * The fields travel as mutation *variables*, not as a closure read.
   *
   * What gets marked saved has to be exactly what was sent. Reading `fields`
   * from the success handler's closure instead makes that depend on which
   * render's closure the query client happens to invoke — and the client does
   * push fresh options into a pending mutation
   * (`MutationObserver.setOptions` → `#currentMutation.setOptions`). Today the
   * captured options win and the two agree; that is an internal, not a promise.
   * Passing the snapshot as an argument makes them the same object by
   * construction, so the question stops mattering.
   *
   * It matters because the failure is silent: an operator who edits a field
   * while the save is in the air would have `savedFields` overwritten with that
   * newer value, `isDirty` would go false, Discard would grey out, and an edit
   * that was never sent would look persisted until the next load dropped it.
   */
  const mutation = useMutation({
    mutationFn: ({ sent, saved }: { sent: ContestEditFields; saved: ContestEditFields }) =>
      contestsApi.update(contestId, {
        // Only when changed: the API refuses either one, even unchanged, once published.
        ...(sent.sportId !== saved.sportId ? { sportId: sent.sportId } : {}),
        ...(sent.reelCount !== saved.reelCount ? { reelCount: Number(sent.reelCount) } : {}),
        name: { en: sent.nameEn },
        superTitle: toNullableString(sent.superTitle),
        subTitle: toNullableString(sent.subTitle),
        description: toNullableString(sent.description),
        date: sent.date.trim() === '' ? undefined : sent.date,
        opensAt: toIsoFromLocalInput(sent.opensAt),
        adapterConfig: buildContestAdapterConfig({
          campaignId: sent.campaignId,
          round: sent.round,
        }),
      }),
    /*
     * A save keeps the operator where they are. The single-page form used to
     * bounce to the Overview, which on a four-step flow would throw away their
     * place in it — and the design puts Save on every screen precisely so it can
     * be used mid-flow.
     */
    onSuccess: async (_contest, { sent }) => {
      setSavedFields(sent);
      setNotice('Changes saved.');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'contests'] });
    },
  });

  const nameEnTrimmed = fields.nameEn.trim();
  const opensAtTrimmed = fields.opensAt.trim();
  const isValid = nameEnTrimmed.length > 0 && isParseableLocalDateTime(opensAtTrimmed);

  const error = mutation.error;
  const errorMessage =
    error instanceof AdminApiError ? error.message : error instanceof Error ? error.message : null;

  const value: ContestEditValue = {
    contestId,
    detail: detail ?? null,
    isLoading,
    loadError,
    isHydrated,

    fields,
    setField: (key) => (value) => {
      setFields((previous) => ({ ...previous, [key]: value }));
      mutation.reset();
    },
    adapterConfigFieldsLocked: detail?.adapterConfigFieldsLocked ?? false,

    nameEnError: requiredTrimmedTextFieldError(
      fields.nameEn,
      nameEnTrimmed,
      blurred.nameEn,
      submitAttempted,
      'English name is required.',
    ),
    opensAtError: requiredLocalDateTimeFieldError(
      opensAtTrimmed,
      blurred.opensAt,
      submitAttempted,
      'Opens at is required.',
      'Enter a valid date and time.',
    ),
    isValid,
    markFieldBlurred: (field) => setBlurred((previous) => ({ ...previous, [field]: true })),
    submitAttempted,

    save: () => {
      setSubmitAttempted(true);
      if (!isValid) return;
      mutation.mutate({ sent: fields, saved: savedFields });
    },
    isSaving: mutation.isPending,
    discard: () => {
      setFields(savedFields);
      setSubmitAttempted(false);
      setBlurred({ nameEn: false, opensAt: false });
      mutation.reset();
    },
    isDirty: (Object.keys(fields) as (keyof ContestEditFields)[]).some(
      (key) => fields[key] !== savedFields[key],
    ),

    notice,
    setNotice,
    errorMessage,
    resetError: () => mutation.reset(),

    detailQueryKey,
  };

  return <ContestEditContext.Provider value={value}>{children}</ContestEditContext.Provider>;
};
